import { fmt } from './constants.js';

export const FEET_PER_METRE = 3.280839895013123;
export const SQ_FEET_PER_SQ_METRE = FEET_PER_METRE ** 2;
export const FOOT_CANDLES_PER_LUX = 1 / 10.763910416709722;
export const MPH_PER_KPH = 0.621371192237334;
export const MPH_PER_MPS = 2.2369362920544;

export const measurementSystem = doc => doc?.opt?.measurementSystem === 'imperial' ? 'imperial' : 'metric';
export const isImperial = doc => measurementSystem(doc) === 'imperial';

const definitions = {
  length: { metric: 'm', imperial: 'ft', metricFactor: 1, imperialFactor: FEET_PER_METRE },
  smallLength: { metric: 'mm', imperial: 'in', metricFactor: 1000, imperialFactor: FEET_PER_METRE * 12 },
  area: { metric: 'm²', imperial: 'ft²', metricFactor: 1, imperialFactor: SQ_FEET_PER_SQ_METRE },
  density: { metric: 'px/m', imperial: 'px/ft', metricFactor: 1, imperialFactor: 1 / FEET_PER_METRE },
  scale: { metric: 'px/m', imperial: 'px/ft', metricFactor: 1, imperialFactor: 1 / FEET_PER_METRE },
  resolution: { metric: 'm/px', imperial: 'ft/px', metricFactor: 1, imperialFactor: FEET_PER_METRE },
  illuminance: { metric: 'lx', imperial: 'fc', metricFactor: 1, imperialFactor: FOOT_CANDLES_PER_LUX },
  speedKph: { metric: 'km/h', imperial: 'mph', metricFactor: 1, imperialFactor: MPH_PER_KPH },
  speedMps: { metric: 'm/s', imperial: 'mph', metricFactor: 1, imperialFactor: MPH_PER_MPS },
  perLength: { metric: '/m', imperial: '/ft', metricFactor: 1, imperialFactor: 1 / FEET_PER_METRE },
  angle: { metric: '°', imperial: '°', metricFactor: 1, imperialFactor: 1 },
  number: { metric: '', imperial: '', metricFactor: 1, imperialFactor: 1 },
};

export function measurementUnit(doc, kind = 'length') {
  const definition = definitions[kind];
  if (!definition) throw new Error(`Unknown measurement kind: ${kind}`);
  return definition[measurementSystem(doc)];
}

export function toDisplay(value, doc, kind = 'length') {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const definition = definitions[kind];
  return n * (isImperial(doc) ? definition.imperialFactor : definition.metricFactor);
}

export function fromDisplay(value, doc, kind = 'length') {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const definition = definitions[kind];
  return n / (isImperial(doc) ? definition.imperialFactor : definition.metricFactor);
}

export function displayStep(step, doc, kind = 'length') {
  if (step === 'any' || step == null) return step;
  const converted = toDisplay(step, doc, kind);
  return Number.isFinite(converted) ? +converted.toPrecision(6) : step;
}

export function inputMeasurement(value, doc, kind = 'length') {
  const converted = toDisplay(value, doc, kind);
  if (!Number.isFinite(converted)) return converted;
  return +converted.toFixed(kind === 'illuminance' ? 3 : 4);
}

export function formatMeasurement(value, doc, kind = 'length', digits = 1, { unit = true } = {}) {
  const converted = toDisplay(value, doc, kind);
  if (!Number.isFinite(converted)) return '—';
  return `${fmt(converted, digits)}${unit ? ` ${measurementUnit(doc, kind)}` : ''}`;
}

export const formatLength = (value, doc, digits = 1, options) => formatMeasurement(value, doc, 'length', digits, options);
export const formatArea = (value, doc, digits = 1, options) => formatMeasurement(value, doc, 'area', digits, options);
export const formatDensity = (value, doc, digits = 0, options) => formatMeasurement(value, doc, 'density', digits, options);
export const formatIlluminance = (value, doc, digits = isImperial(doc) ? 2 : 0, options) => formatMeasurement(value, doc, 'illuminance', digits, options);
export const formatSpeedKph = (value, doc, digits = 1, options) => formatMeasurement(value, doc, 'speedKph', digits, options);
export const formatSpeedMps = (value, doc, digits = 1, options) => formatMeasurement(value, doc, 'speedMps', digits, options);

export function measurementNumber(value, doc, kind) {
  const converted = toDisplay(value, doc, kind);
  if (!Number.isFinite(converted)) return 'unknown';
  if (kind === 'illuminance') return converted < 1 ? converted.toFixed(2) : Math.round(converted).toLocaleString();
  return String(converted);
}

export function formatMeasurementRange(low, high, doc, kind = 'illuminance') {
  const a = toDisplay(low, doc, kind), b = toDisplay(high, doc, kind);
  const range = Math.abs(b - a) <= Math.max(.01, Math.abs(b) * .001) && Number.isFinite(b)
    ? measurementNumber(low, doc, kind)
    : `${measurementNumber(low, doc, kind)}–${measurementNumber(high, doc, kind)}`;
  return `${range} ${measurementUnit(doc, kind)}`;
}

export function measurementLabel(label, doc, kind = 'length') {
  return `${label} · ${measurementUnit(doc, kind)}`;
}

export function convertInputProps({ value, min, max, step }, doc, kind) {
  return {
    value: value == null ? value : inputMeasurement(value, doc, kind),
    min: min == null ? min : toDisplay(min, doc, kind),
    max: max == null ? max : toDisplay(max, doc, kind),
    step: displayStep(step, doc, kind),
  };
}
